import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Layout from "../components/layout";
import { config } from "../utils/constants";
import videoSource from "../assets/videos/hair_story_advanced_with_subtitle.mp4";
import { faHeart as faHeartFilled } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartOutline } from "@fortawesome/free-regular-svg-icons";
import PromoOffer from "../components/promoOffer";
import { useRandomScrollPromo } from "../hooks/useRandomScrollPromo";
import hero1_left from '../assets/images/home/image-1-3.jpg';
import hero1_right from '../assets/images/home/2.jpg';
import hero2_left from '../assets/images/home/image-2-3.jpg';
import hero2_right from '../assets/images/home/2-cleanser.jpg';
import { Helmet } from "react-helmet-async";
import iconBlack from "../assets/images/logos/mountain-black-451x512.png";

const ProductCard = ({ defaultImage, hoverImage, title, description, isInView }) => {
  const [liked, setLiked] = useState(false);
  const handleLike = () => setLiked((v) => !v);

  return (
    <div className={`card product-card ${isInView ? "fly-in" : ""}`}>
      <div className="image-container">
        <img src={defaultImage} alt={title} className="default-image card-img-top" />
        <img src={hoverImage} alt={`${title} Hover`} className="hover-image card-img-top" />
      </div>
      <div className="card-body text-container">
        <div className="like-icon" onClick={handleLike}>
          <FontAwesomeIcon icon={liked ? faHeartFilled : faHeartOutline} />
        </div>
        <h5 className="card-title">{title}</h5>
        <p className="card-text">{description}</p>
      </div>
    </div>
  );
};

const SignupSchema = Yup.object().shape({
  firstName: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("First name is required"),
  lastName: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
});


const Home = () => {
  const BRAND = "#001f3f";
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("CID");
  const [isSticky, setIsSticky] = useState(false);
  const dataFetchedRef = useRef(false);

  const heroRef = useRef(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const onTrigger = useCallback(() => setPromoOpen(true), []);  
  const ogImage = "https://molu.co/assets/images/og/molu-banner-1200x630.png";
  
  useRandomScrollPromo({
    onTrigger,
    minTimeMs: 8000,
    minScrollPx: 800,
    probability: 0.8,
    checkThrottleMs: 1500,
    cooldownMs: 3 * 60 * 1000,
    dailyCap: 2,
    sessionCap: 2,
    storageKeyBase: "promo_random_home",
  });

  useEffect(() => {
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;
    const source = campaignId ? config.funnel[campaignId.split("-")[0]] : "Direct visit";
    const campaign_id = campaignId ? campaignId.split("-")[1] : "None";

    ReactGA.send({
      hitType: "pageview",
      page: `/${campaignId || ""}`,
      title: "Page Visit",
      customData: `source: ${source}, campaign_id: ${campaign_id}`,
    });
  }, [campaignId]);

  const REACT_APP_API_URL = config.url.REACT_APP_API_URL;
  const REACT_API_IMAGE_URL = config.url.REACT_API_IMAGE_URL;
  const [families, setFamilies] = useState([]);
  const [serverMessage, setServerMessage] = useState(null);

  useEffect(() => {
    axios
      .get(`${REACT_APP_API_URL}/products/families/`, { params: { page_size: 48 } })
      .then((res) => {
        const data = res.data?.results ?? res.data ?? [];
        setFamilies(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Error fetching product families:", err);
      });
  }, [REACT_APP_API_URL]);

  const isLocalDevHost = (host) =>
    /^(localhost|127\.0\.0\.1|0\.0\.0\.0|.+\.local|.+\.test)$/i.test(host || "");

  const toHttpsIfNeeded = (u) => {
    try {
      const url = new URL(u);
      const pageIsHttps = typeof window !== "undefined" && window.location.protocol === "https:";
      if (pageIsHttps && url.protocol === "http:" && !isLocalDevHost(url.hostname)) {
        url.protocol = "https:";
      }
      return url.toString();
    } catch {
      return u;
    }
  };

  const joinPath = (base, path) => {
    const b = String(base || "").replace(/\/+$/, "");
    const p = String(path || "").replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const asUrl = (path) => {
    if (!path) return "/default.jpg";

    // Absolute URL: maybe upgrade depending on host
    if (/^https?:\/\//i.test(path)) {
      return toHttpsIfNeeded(path);
    }

    // Relative path: build from your image base
    const base = String(REACT_API_IMAGE_URL || "").replace(/\/+$/, "");
    const rel  = String(path).replace(/^\/+/, "");
    return toHttpsIfNeeded(`${base}/${rel}`);
  };

  const pickPrimaryImage = (media = []) => {
    const img = media.find((m) => m.media_type === "image" && m.image)?.image;
    if (img) return asUrl(img);
    const poster = media.find((m) => m.poster)?.poster;
    if (poster) return asUrl(poster);
    return "/default.jpg";
  };

  const handleSubmit = (values, { resetForm }) => {
    axios
      .post(
        `${REACT_APP_API_URL}/capture/`,
        { first_name: values.firstName, last_name: values.lastName, email: values.email, type: "info" },
        { withCredentials: true }
      )
      .then((res) => {
        setServerMessage(res.data.message);
        resetForm();
        ReactGA.event({
          category: "engagement",
          action: "form submission",
          label: "Home Page Info Form",
        });
      })
      .catch((err) => {
        if (err.response && err.response.status === 409) {
          setServerMessage("Thank you. We will let you know when our bags will be available.");
        }
      });
  };

  const [isAboutVisible, setIsAboutVisible] = useState(false);
  const [isCollectionTitleVisible, setIsCollectionTitleVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const targetId = entry.target.id;
          const index = entry.target.getAttribute("data-index");
          if (entry.isIntersecting) {
            if (targetId === "collection-title") setIsCollectionTitleVisible(true);
            if (index !== null) setVisibleCards((prev) => [...new Set([...prev, index])]);
            if (targetId === "about") setIsAboutVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    const aboutSection = document.querySelector("#about");
    if (aboutSection) observer.observe(aboutSection);
    const collectionTitle = document.querySelector("#collection-title");
    if (collectionTitle) observer.observe(collectionTitle);
    const productCards = document.querySelectorAll(".product-card");
    productCards.forEach((card, index) => {
      card.setAttribute("data-index", index);
      observer.observe(card);
    });

    return () => {
      if (aboutSection) observer.unobserve(aboutSection);
      if (collectionTitle) observer.unobserve(collectionTitle);
      productCards.forEach((card) => observer.unobserve(card));
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { threshold: 0.88 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => {
      if (heroRef.current) observer.unobserve(heroRef.current);
    };
  }, []);

  // ---------- HERO VIDEO: prevent multiple instances playing after back-click ----------
  const videoRef = useRef(null);
  const playedOnceRef = useRef(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Pause any other videos (in case another page left one running)
    document.querySelectorAll("video").forEach((el) => {
      if (el !== v) {
        try { el.pause(); } catch {}
      }
    });

    // Make autoplay reliable
    v.muted = true;        // attribute also set on element for iOS
    v.playsInline = true;

    if (!playedOnceRef.current) {
      v.play().catch(() => {});
      playedOnceRef.current = true;
    }

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        try { v.pause(); } catch {}
      }
    };

    const onPageHide = () => {
      try { v.pause(); } catch {}
    };

    // Fully unload on leave to avoid ghost playback
    const unload = () => {
      try {
        v.pause();
        v.removeAttribute("src");
        v.load();
      } catch {}
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", unload);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", unload);
      unload(); // pause & detach when Home unmounts
    };
  }, []);

  const navigate = useNavigate();
  const handleClick = () => navigate("/ten-power-foods");
// Helpers near your other helpers
const imageUrlsFromVariant = (variant) => {
  const media = Array.isArray(variant?.media) ? variant.media : [];
  // images only
  const imgs = media
    .filter((m) => m.media_type === "image" && m.image)
    .map((m) => asUrl(m.image));
  return imgs;
};

const [featuredHero, setFeaturedHero] = useState(null);

useEffect(() => {
  if (!Array.isArray(families) || families.length === 0) return;

  // Collect all variants marked featured across all families
  const featured = [];
  for (const fam of families) {
    const variants = fam?.variants || {};
    for (const vid of Object.keys(variants)) {
      const v = variants[vid];
      if (v?.is_featured_product === true) {
        featured.push({ fam, variant: v, variantId: vid });
      }
    }
  }

  if (featured.length === 0) {
    // No featured product — optional: use first available variant instead
    const firstFam = families[0];
    const defaultId = String(firstFam?.default_variant_id || "");
    const defVar = firstFam?.variants?.[defaultId];
    if (!defVar) return;

    const imgs = imageUrlsFromVariant(defVar);
    const right = imgs[1] || imgs[0] || "/default.jpg"; // 2nd image preferred
    const left  = imgs[2] || imgs[0] || "/default.jpg"; // 3rd image preferred

    setFeaturedHero({
      rightSrc: right,
      leftSrc: left,
      famName: firstFam?.name || "",
      to: `/shop/product/${defaultId}`,
    });
    return;
  }

  // Pick a random featured variant
  const pick = featured[Math.floor(Math.random() * featured.length)];
  const imgs = imageUrlsFromVariant(pick.variant);

  // Pick the second image for right, third for left (with fallbacks)
  const right = imgs[1] || imgs[0] || "/default.jpg";
  const left  = imgs[2] || imgs[0] || "/default.jpg";
  setFeaturedHero({
    rightSrc: right,
    leftSrc: left,
    famName: pick.fam?.name || "",
    to: `/shop/product/${pick.variantId}`,
  });
}, [families]);  // rerun when families load
  return (
    <div className="home-content">
      <Layout>
        <Helmet prioritizeSeoTags>
          {/* Basic SEO */}
          <title>Copper Peptides & 2DDR for Hair Growth | Molu Hair Growth Serum</title>
          <meta
            name="description"
            content="Molu Hair Growth blends copper peptides and 2-deoxy-D-ribose for strong, thick, and healthy hair growth."
          />
          <meta
            name="keywords"
            content="copper peptides for hair growth, 2-deoxy-d-ribose for hair growth, 2ddr for hair growth"
          />
          <link
            rel="canonical"
            href={(typeof window !== "undefined" ? window.location.origin : "https://molu.co") + "/"}
          />

          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content="Copper Peptides & 2DDR for Hair Growth | Molu Hair Growth Serum" />
          <meta property="og:description" content="Molu Hair Growth blends copper peptides and 2-deoxy-D-ribose for strong, thick, and healthy hair growth." />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:url" content={(typeof window !== "undefined" ? window.location.href : "")} />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Copper Peptides & 2DDR for Hair Growth | Molu Hair Growth Serum" />
          <meta name="twitter:description" content="Molu Hair Growth blends copper peptides and 2-deoxy-D-ribose for strong, thick, and healthy hair growth." />
          <meta name="twitter:image" content={ogImage} />

            {/* JSON-LD: WebSite (with optional SearchAction) */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              name: "Molu",
              url: (typeof window !== "undefined" ? window.location.origin : "https://molu.co") + "/",
              potentialAction: {
                "@type": "SearchAction",
                target:
                  (typeof window !== "undefined" ? window.location.origin : "https://molu.co") +
                  "/search?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            })}
          </script>

          {/* JSON-LD: Organization */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Molu",
              url: (typeof window !== "undefined" ? window.location.origin : "https://molu.co") + "/",
              logo: iconBlack, // better: a square transparent logo (e.g., 512x512 PNG)
              sameAs: [
                "https://www.youtube.com/channel/UCUVzLhFqQzavjNhlsPOH4Bg",
                "https://www.facebook.com/people/Molu-Hair-Growth/61576808587286/",
                "https://www.instagram.com/moluhairgrowth"
              ],
              brand: {
                "@type": "Brand",
                name: "Molu",
              },
            })}
          </script>

          {/* JSON-LD: FAQPage (matches your on-page FAQ content) */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "How long until I see results?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Most users begin to notice visible improvements within 60 days of consistent daily use, though results may vary.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Is it safe for daily use?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes. Molu is formulated for gentle, daily topical use without harsh chemicals or hormones.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Can women use it?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Absolutely. Molu is safe for both men and women and suitable for various hair types and thinning patterns.",
                  },
                },
                {
                  "@type": "Question",
                  name: "What if it doesn't work for me?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "We offer a 90-day money-back guarantee if you’re not satisfied with your results. See our Terms and Conditions.",
                  },
                },
                {
                  "@type": "Question",
                  name: "How is this different from minoxidil?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Our formula features three breakthrough ingredients working together: 2-deoxy-d-ribose (2dDR) for cellular energy enhancement, copper peptides (GHK-Cu) for follicle regeneration, and Lactate-Pyruvate complex for metabolic pathway activation. This research-inspired combination targets multiple biological pathways rather than relying on single-ingredient approaches.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Are there side effects?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "No side effects have been reported. Molu contains no synthetic hormones.",
                  },
                },
                {
                  "@type": "Question",
                  name: "How much product do I need?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "One bottle typically lasts 30 days when used once daily on targeted thinning areas.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Can I use it with other treatments?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Yes, Molu can be used alongside other treatments like shampoos, supplements, or microneedling, but we recommend testing for skin sensitivity.",
                  },
                },
              ],
            })}
          </script>
        </Helmet>
        {/* Hero Section */}
        <section ref={heroRef} className="hero">
          <div className="video-container">
            <video
              ref={videoRef}
              autoPlay
              loop
              controls
              playsInline
              muted
              className="hero-video"
            >
              <source src={videoSource} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="video-overlay">
              <p className="hero-subtitle"></p>
              <a href="#products" className="btn btn-primary">
                Explore Collection
              </a>
            </div>
          </div>
        </section>

        {/* Products */}
        <section id="products" className="py-5 text-center bg-white text-dark">
          <div className="container">
            <h1 className="display-5 fw-bold mb-5">Top Sellers</h1>

            <div className="row isotope-grid">
              {/* Families scroller: shows 3, scrolls horizontally */}
              <div className="family-grid">
                {families
                  .filter((fam) => fam?.display_on_home_page === true)
                  .slice(0, 3)
                  .map((fam) => {
                    const defaultId = String(fam.default_variant_id || "");
                    const defVar = fam.variants?.[defaultId];
                    const imgUrl = pickPrimaryImage(defVar?.media || []);
                    const blurb = fam.blurb || "";

                    return (
                      <div className="family-card" key={fam.slug || defaultId}>
                        <div
                          className="block2 h-100 d-flex flex-column hov-img0 molu-card"
                          role="link"
                          tabIndex={0}
                          onClick={() => navigate(`/shop/product/${defaultId}`)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/shop/product/${defaultId}`);
                            }
                          }}
                        >
                          <div className="block2-pic">
                            <img src={imgUrl} alt={fam.name} className="product-img-size" />
                          </div>

                          <div className="block2-txt p-t-14 d-flex flex-column align-items-center text-center flex-grow-1">
                            <div className="molu-textbox d-flex flex-column align-items-center text-center w-100">
                              <p className="molu-name fw-bold mb-2" style={{ color: BRAND }}>
                                {fam.name}
                              </p>
                              {blurb && <p className="molu-blurb mb-3 px-2">{blurb}</p>}
                            </div>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/shop/product/${defaultId}`);
                              }}
                              className="explore-btn mt-auto mb-3 px-3 py-2 fw-semibold"
                              aria-label={`Explore ${fam.name}`}
                            >
                              Explore
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </section>
        {/* ====== Split Image Section (below products) ====== */}
        <div className="container text-center mb-5 pb-5">

            <h1 className="display-5 fw-bold mb-2">Featured Product</h1>
        </div>
        <section className="split-hero box-shadow-below" aria-label="Featured visuals">
          <div className="split-hero__col">
            <img
              src={featuredHero?.leftSrc || "/default.jpg"}
              alt="Featured left"
              loading="lazy"
              className="split-hero__img"  // ← contain on left
            />
          </div>

          <div className="split-hero__col">
            <img
              src={featuredHero?.rightSrc || "/default.jpg"}
              alt="Featured right"
              loading="lazy"
              className="split-hero__img split-hero__img--contain"  // cover on right (default)
            />
          </div>
        </section>      
        {/* Product Highlights */}
        <section className="py-5 text-center bg-white text-dark">
          <div className="container">
            <h1 className="display-5 fw-bold mb-3">Regrow Your Hair. Reclaim Your Confidence.</h1>

            <p className="lead mb-4">
              The hair growth gel powered by 2-deoxy-D-ribose — proven to reactivate dormant follicles at the cellular level.
            </p>

            <ul className="list-unstyled mb-4 mx-auto" style={{ maxWidth: "500px" }}>
              <li className="mb-2">✓ 78% more healthy hair-growing cells</li>
              <li className="mb-2">✓ Results without the side effects of monixidil and finesteride (erectile dysfunction, skin irritation)</li>
              <li className="mb-2">✓ Visible results in 60 days (most individuals)</li>
              <li className="mb-2">✓ Clinically proven ingredients</li>
            </ul>

            <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3 mb-3">
              <button
                className="btn btn-warning text-white px-4 py-2 fw-semibold"
                onClick={() => (window.location.href = `${REACT_APP_API_URL}/start/hair-health-assessment`)}
              >
                Start Your Hair Assessment
              </button>
              <button className="btn btn-outline-dark px-4 py-2 fw-semibold" onClick={handleClick}>
                Learn About Hair Growth Nutrition
              </button>
            </div>

            <div className="text-muted mt-3">
              ✅ 90-Day Money-Back Guarantee <Link to="/terms-and-conditions">Terms and Conditions</Link>
            </div>
          </div>

          <PromoOffer open={promoOpen} onOpenChange={setPromoOpen} tabText="Get 20% Off" imageAlt="New customer discount" />
        </section>

        {/* Signup Section */}
        <div className="">
          <section className="signup py-5" id="signup-anchor">
            <div className="container">
              {serverMessage ? (
                <div className="text-center">
                  <h2 className="text-success">{serverMessage}</h2>
                </div>
              ) : (
                <>
                  <h2 className="text-center mb-4">Sign up for our newsletter and new product updates!</h2>
                  <Formik
                    initialValues={{ firstName: "", lastName: "", email: "" }}
                    validationSchema={SignupSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form className="signup-form">
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <Field type="text" name="firstName" className="form-control" placeholder="First Name" />
                            <ErrorMessage name="firstName" component="div" className="text-danger mt-1" />
                          </div>
                          <div className="col-md-6">
                            <Field type="text" name="lastName" className="form-control" placeholder="Last Name" />
                            <ErrorMessage name="lastName" component="div" className="text-danger mt-1" />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-12">
                            <Field type="email" name="email" className="form-control" placeholder="Email Address" />
                            <ErrorMessage name="email" component="div" className="text-danger mt-1" />
                          </div>
                        </div>
                        <div className="text-center">
                          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            Sign Up
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </>
              )}
              <p className="text-center mb4 p-t-20">
                We respect your inbox and your privacy. Your information is secure and will only be used to send you valuable hair health insights. No spam, no
                sharing, ever.
              </p>
            </div>
          </section>
        </div>

        {/* About Section */}
        <section className="py-5 bg-white text-dark">
          <div className="container text-center">
            <h2 className="mb-4 fw-bold">Scientifically Backed Comparison of Hair Loss Treatments</h2>

            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Criteria</th>
                    <th className="text-warning fw-semibold">Molu Advaced Hair Growth Fuel</th>
                    <th>Minoxidil</th>
                    <th>Finasteride</th>
                    <th>Hair Transplant</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Regrowth Evidence</strong></td>
                    <td>✅ Results reported as early as 60 days</td>
                    <td>✅ FDA-approved, moderate efficacy</td>
                    <td>✅ FDA-approved, high efficacy in men</td>
                    <td>✅ Immediate visible results (after healing)</td>
                  </tr>
                  <tr>
                    <td><strong>Onset of Results</strong></td>
                    <td>⏳ 60 days - 6 months</td>
                    <td>⏳ 3–6 months</td>
                    <td>⏳ 3–6 months</td>
                    <td>✅ Immediate (after 2–4 weeks recovery)</td>
                  </tr>
                  <tr>
                    <td><strong>Application Type</strong></td>
                    <td>✅ Topical, once or twice daily</td>
                    <td>❌ Must apply twice daily</td>
                    <td>✅ Oral pill, once daily</td>
                    <td>❌ Surgical procedure, local anesthesia</td>
                  </tr>
                  <tr>
                    <td><strong>Initial Shedding</strong></td>
                    <td>✅ None reported</td>
                    <td>⚠️ Common in early stages</td>
                    <td>✅ No initial shedding</td>
                    <td>✅ None</td>
                  </tr>
                  <tr>
                    <td><strong>Known Side Effects</strong></td>
                    <td>✅ None reported</td>
                    <td>⚠️ Scalp irritation, flaking, dryness</td>
                    <td>❌ Erectile dysfunction, low libido, mood changes</td>
                    <td>⚠️ Infection, scarring, poor graft survival</td>
                  </tr>
                  <tr className="table-warning fw-bold">
                    <td colSpan="5">
                      💡 <strong>Molu's Advantage:</strong> The advanced Triple-Action formula powered by <em>2-Deoxy-D-Ribose, GHK-Cu Copper Peptides, and Sodium Lactate & Pyruvate Complex</em> - three research-backed ingredients shown in studies to support cellular energy production, follicle regeneration, and stem cell activation without hormonal side effects.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
{/* FAQ Section */}
<section id="faq" className="py-5">
  <div className="container">
    <h2 className="text-center mb-4 fw-bold">Common Questions About Molu</h2>
    <div className="accordion" id="faqAccordion">

      {/* FAQ 1 */}
      <div className="accordion-item">
        <h2 className="accordion-header" id="q1">
          <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#a1" aria-expanded="true" aria-controls="a1">
            How long until I see results?
          </button>
        </h2>
        <div id="a1" className="accordion-collapse collapse show" aria-labelledby="q1" data-bs-parent="#faqAccordion">
          <div className="accordion-body">
            Most users begin to notice visible improvements within 60 days of consistent daily use, though results may vary.
          </div>
        </div>
      </div>

      {/* FAQ 2 */}
      <div className="accordion-item">
        <h2 className="accordion-header" id="q2">
          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#a2" aria-expanded="false" aria-controls="a2">
            Is it safe for daily use?
          </button>
        </h2>
        <div id="a2" className="accordion-collapse collapse" aria-labelledby="q2" data-bs-parent="#faqAccordion">
          <div className="accordion-body">
            Yes. Molu is formulated for gentle, daily topical use without harsh chemicals or hormones.
          </div>
        </div>
      </div>

      {/* FAQ 3 */}
      <div className="accordion-item">
        <h2 className="accordion-header" id="q3">
          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#a3" aria-expanded="false" aria-controls="a3">
            Can women use it?
          </button>
        </h2>
        <div id="a3" className="accordion-collapse collapse" aria-labelledby="q3" data-bs-parent="#faqAccordion">
          <div className="accordion-body">
            Absolutely. Molu is safe for both men and women and suitable for various hair types and thinning patterns.
          </div>
        </div>
      </div>

      {/* FAQ 4 */}
      <div className="accordion-item">
        <h2 className="accordion-header" id="q4">
          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#a4" aria-expanded="false" aria-controls="a4">
            What if it doesn't work for me?
          </button>
        </h2>
        <div id="a4" className="accordion-collapse collapse" aria-labelledby="q4" data-bs-parent="#faqAccordion">
          <div className="accordion-body">
            We offer a 90-day money-back guarantee if you’re not satisfied with your results. <a href="/terms-and-conditions">Terms and Conditions</a>
          </div>
        </div>
      </div>

      {/* FAQ 5 */}
      <div className="accordion-item">
        <h2 className="accordion-header" id="q5">
          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#a5" aria-expanded="false" aria-controls="a5">
            How is this different from minoxidil?
          </button>
        </h2>
        <div id="a5" className="accordion-collapse collapse" aria-labelledby="q5" data-bs-parent="#faqAccordion">
          <div className="accordion-body">
            Our formula features three breakthrough ingredients working together: 2-deoxy-d-ribose (2dDR) for cellular energy enhancement, copper peptides (GHK-Cu) for follicle regeneration, and Lactate-Pyruvate complex for metabolic pathway activation. This research-inspired combination targets multiple biological pathways rather than relying on single-ingredient approaches.
          </div>
        </div>
      </div>

      {/* FAQ 6 */}
      <div className="accordion-item">
        <h2 className="accordion-header" id="q6">
          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#a6" aria-expanded="false" aria-controls="a6">
            Are there side effects?
          </button>
        </h2>
        <div id="a6" className="accordion-collapse collapse" aria-labelledby="q6" data-bs-parent="#faqAccordion">
          <div className="accordion-body">
            No side effects have been reported. Molu contains no synthetic hormones.
          </div>
        </div>
      </div>

      {/* FAQ 7 */}
      <div className="accordion-item">
        <h2 className="accordion-header" id="q7">
          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#a7" aria-expanded="false" aria-controls="a7">
            How much product do I need?
          </button>
        </h2>
        <div id="a7" className="accordion-collapse collapse" aria-labelledby="q7" data-bs-parent="#faqAccordion">
          <div className="accordion-body">
            One bottle typically lasts 30 days when used once daily on targeted thinning areas.
          </div>
        </div>
      </div>

      {/* FAQ 8 */}
      <div className="accordion-item">
        <h2 className="accordion-header" id="q8">
          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#a8" aria-expanded="false" aria-controls="a8">
            Can I use it with other treatments?
          </button>
        </h2>
        <div id="a8" className="accordion-collapse collapse" aria-labelledby="q8" data-bs-parent="#faqAccordion">
          <div className="accordion-body">
            Yes, Molu can be used alongside other treatments like shampoos, supplements, or microneedling, but we recommend testing for skin sensitivity.
          </div>
        </div>
      </div>

    </div>
  </div>
</section>

        {/* Off-white Section Above Footer */}
        <div className="footer-above py-4">
          <div className="container text-center"></div>
        </div>
      </Layout>
    </div>
  );
};

export default Home;
